/**
 * Domain types mirroring the backend entities and the /v1 REST contract.
 * These are the only shapes the UI knows about; no vendor types leak here.
 */

// ---- Shared primitives -----------------------------------------------------

export type UUID = string;
/** ISO-8601 timestamp string (timestamptz on the wire). */
export type ISODateTime = string;
/** ISO-8601 date string (YYYY-MM-DD). */
export type ISODate = string;

export type Role = 'admin' | 'editor' | 'member';

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PageParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  /** Restrict the listing to one knowledge area (slug). ANDed with `search`. */
  area?: string;
  /**
   * Documents only. `mine` (the default) is the reader's own shelf — plus the
   * global acervo, for an admin, the only role that reaches it; `all` is the
   * cross-owner INVENTORY and is admin-only too — the API answers 403 to
   * anybody else, which is why the console only offers it to an admin instead of
   * letting the request fail.
   */
  scope?: 'mine' | 'all';
}

// ---- Knowledge -------------------------------------------------------------

export type DocType = 'spec' | 'policy' | 'faq' | 'guide' | 'glossary' | 'other';

/**
 * One entry of the knowledge-area vocabulary (`GET /v1/knowledge/areas`).
 *
 * The slug is deliberately a bare `string` and not a closed union: the taxonomy
 * lives in the backend and is served, so a new area must reach the console by
 * deploying the API — never by shipping the frontend again. `family` gives one
 * level of hierarchy for free (`salesforce`, `padroes`, `interno`) so the UI can
 * group without a second request, and `documentCount` is what the list shows —
 * zero included, because an empty area is a fact worth seeing.
 *
 * `ownedByMe` marks the reader's PERSONAL areas (family `pessoal`): named by
 * them, listed only to them, usable only on their own documents — never on a
 * global one — and the only areas they may rename or delete. The API lists them
 * first.
 */
export interface KnowledgeArea {
  slug: string;
  label: string;
  family: string;
  description: string;
  documentCount: number;
  ownedByMe: boolean;
  /**
   * The profile this area belongs to, or `null` for a personal one. The platform
   * taxonomy is partitioned by profile: a member sees their own profile's areas
   * plus `padrao`, an admin sees every profile (or one, with `?profile=`).
   */
  profile: string | null;
  kind: AreaKind;
  /** The parent area's slug for a subarea — platform or personal; `null` at the top level. */
  parentSlug: string | null;
  /** The locked "Geral" subarea every area is born with — never renamed nor deleted. */
  isGeneral: boolean;
  sortOrder: number;
}

/**
 * Where a node sits in the two-level taxonomy. Only a LEAF may hold a document:
 * a `subarea` or a `personal` area. Filing under an `area` is refused (422) with
 * the sentence that names its Geral.
 */
export type AreaKind = 'area' | 'subarea' | 'personal';

/** How many personal areas one person may keep — the API's own ceiling. */
export const MAX_PERSONAL_AREAS = 20;
/** How many subareas one personal area may hold — the API's own ceiling. */
export const MAX_PERSONAL_SUBAREAS_PER_AREA = 20;

// ---- Taxonomy (admin) ------------------------------------------------------

/**
 * One node of the admin taxonomy tree, area or subarea alike. The slug is
 * DERIVED by the server from the label at creation and never changes afterwards
 * — documents are filed by slug, so a rename that moved it would orphan them.
 */
export interface TaxonomyNode {
  id: UUID;
  slug: string;
  label: string;
  /** What the node holds, in the vocabulary a question would use: this is the
   *  signal the area router reads, which is why the admin form insists on it. */
  description: string;
  enabled: boolean;
  isGeneral: boolean;
  sortOrder: number;
  documentCount: number;
}

/** An area with its subareas — the Geral among them, first and locked. */
export interface TaxonomyArea extends Omit<TaxonomyNode, 'isGeneral'> {
  profile: string;
  subareas: TaxonomyNode[];
}

/** `GET /v1/taxonomy/areas` — the tree plus the profiles it is partitioned by. */
export interface TaxonomyResponse {
  profiles: { slug: string; label: string }[];
  areas: TaxonomyArea[];
}

export interface TaxonomyAreaCreate {
  profile: string;
  label: string;
  description?: string;
  sortOrder?: number;
}

export interface TaxonomySubareaCreate {
  label: string;
  description?: string;
  sortOrder?: number;
}

/** Partial update of any node. A Geral accepts only `description`/`sortOrder`. */
export interface TaxonomyNodePatch {
  label?: string;
  description?: string;
  enabled?: boolean;
  sortOrder?: number;
}

// ---- Governance: profiles --------------------------------------------------

/** Who a profile may be assigned to: an account, a dependent, or both. */
export type ProfileAssignableTo = 'user' | 'dependent' | 'both';

/**
 * Whose base a registered WhatsApp group answers from: only the owner's
 * (`owner_only`, the platform default) or every member's public shelf
 * (`all_members`, what Business buys).
 */
export type ProfileGroupMode = 'owner_only' | 'all_members';

/**
 * The capability switches a profile turns on. Closed on purpose: the API 422s
 * an unknown key (`recurso desconhecido: {key}`) rather than storing a flag no
 * runtime reads, so the admin panel renders exactly these seven.
 */
export interface ProfileFeatures {
  vision: boolean;
  audio: boolean;
  imageGeneration: boolean;
  messageRelay: boolean;
  reminders: boolean;
  examBoards: boolean;
  /** O agente envia pelo WhatsApp o arquivo original de um documento da base. */
  attachments: boolean;
}

/** The feature keys in the order the admin panel shows them. */
export const PROFILE_FEATURE_KEYS: (keyof ProfileFeatures)[] = [
  'vision',
  'audio',
  'imageGeneration',
  'messageRelay',
  'reminders',
  'examBoards',
  'attachments',
];

/**
 * A profile: one administrable package of quotas, capabilities and taxonomy,
 * assigned to a user (or a dependent). The group limit, the group mode and the
 * private-document quota are READ from this row at runtime, so raising
 * somebody's ceiling is an edit, not a deploy.
 */
export interface Profile {
  slug: string;
  label: string;
  description: string;
  assignableTo: ProfileAssignableTo;
  groupLimit: number;
  groupMode: ProfileGroupMode;
  privateQuota: number;
  dependentsLimit: number;
  userPersonasLimit: number;
  /** Imagens por dia por pessoa na conversa direta (0 a 100; 0 desliga esse canal). */
  imageDailyLimit: number;
  /**
   * Imagens por dia de cada grupo, contadas no grupo e não em quem pede: vale o
   * perfil do DONO, que liga o recurso e paga a conta (0 a 100; 0 desliga).
   */
  groupImageDailyLimit: number;
  features: ProfileFeatures;
  /**
   * A voz oficial do perfil: um Agente padrão ({@link PersonaKind}). Não existe
   * mais "padrão da plataforma", então o `PATCH` recusa `null` (422); o tipo
   * segue anulável só porque a coluna é, e um perfil sem voz é um defeito que o
   * console mostra em vez de esconder.
   */
  defaultPersonaId: string | null;
  enabled: boolean;
  sortOrder: number;
  /** How many accounts hold it — what makes a deletion refusable, and why. */
  userCount: number;
}

export interface ProfileCreate {
  slug: string;
  label: string;
  description?: string;
  assignableTo?: ProfileAssignableTo;
  groupLimit?: number;
  groupMode?: ProfileGroupMode;
  privateQuota?: number;
  dependentsLimit?: number;
  userPersonasLimit?: number;
  imageDailyLimit?: number;
  groupImageDailyLimit?: number;
  features?: Partial<ProfileFeatures>;
  /** A voz oficial do perfil novo: um Agente padrão habilitado. */
  defaultPersonaId?: string;
  /** Ausente, o perfil nasce habilitado. O clone manda o do perfil de origem. */
  enabled?: boolean;
  sortOrder?: number;
}

/** Partial update; `enabled` included — a profile is retired, never orphaned. */
export type ProfilePatch = Partial<Omit<Profile, 'slug' | 'userCount'>>;

/**
 * `GET /v1/profiles/{slug}/deletion-impact` (admin): o que a exclusão vai mover,
 * contado pelo servidor. Os usuários, as áreas e os especialistas vão para o
 * `padrao`; os dependentes, para o perfil que o admin escolher, e por isso
 * `needsDependentsTarget` diz se a confirmação precisa pedir esse destino.
 */
export interface ProfileDeletionImpact {
  users: number;
  dependents: number;
  areas: number;
  specialists: number;
  needsDependentsTarget: boolean;
}

/** `DELETE /v1/profiles/{slug}`: o perfil que herda os dependentes, quando há. */
export interface ProfileDeleteInput {
  slug: string;
  dependentsTo?: string;
}

/**
 * `GET /v1/profiles/assignable` — the picker projection every member may read:
 * enough to choose, never the quotas behind the choice.
 */
export interface AssignableProfile {
  slug: string;
  label: string;
  description: string;
  /**
   * Os recursos que o perfil liga. Vêm no picker porque quem escolhe um perfil
   * para um dependente está escolhendo o que ele vai poder fazer — e a lista da
   * Família mostra isso em chips sem pedir nada a mais ao servidor.
   */
  features: ProfileFeatures;
}

/** What the signed-in profile allows — the runtime half of {@link Profile}. */
export interface UserCapabilities {
  groupLimit: number;
  groupMode: ProfileGroupMode;
  privateQuota: number;
  dependentsLimit: number;
  userPersonasLimit: number;
  features: ProfileFeatures;
}

export interface Document {
  id: UUID;
  source: string;
  title: string;
  /** Storage key of the retained file, or the bare filename for older uploads. */
  uri: string | null;
  /** Null when the upload carried no type (the form no longer asks for one). */
  docType: DocType | null;
  /**
   * The document's knowledge area (slug), or null when it was never classified.
   * Null is not a defect: an unclassified document is GLOBAL — it stays visible
   * to every question and is only ever hidden by an explicit area filter.
   */
  area: string | null;
  version: string | null;
  ownerTeam: string | null;
  checksum: string | null;
  ingestedAt: ISODateTime | null;
  /** Last (re)indexing moment — bumped by every reindex; what "Indexado" shows. */
  indexedAt: ISODateTime | null;
  chunkCount: number;
  /**
   * Whether the API kept this document's source file and can sign a download for
   * it. False for documents ingested before retention existed (and for metadata-only
   * registrations): the console hides the action rather than offering a dead link.
   */
  downloadable: boolean;
  /**
   * Whether this row is on the reader's OWN shelf (`true`) or in the shared
   * global corpus (`false`). A listing only ever contains those two kinds — a
   * document belonging to somebody else is not in the page at all. Only an
   * admin's listing holds `false` rows: everybody else's is their own shelf and
   * nothing more. WHO the document answers is {@link Document.visibility}.
   */
  ownedByMe: boolean;
  /** Who the document answers — see {@link DocumentVisibility}. An owned row is
   *  `private` or `public`; a row with no owner is always `global`. */
  visibility: DocumentVisibility;
  /**
   * Who holds this document — filled ONLY in the admin inventory (`scope=all`),
   * where a row can belong to a third person and `ownedByMe` stops being the
   * whole answer. Null in every ordinary listing, and null for a global row even
   * inside the inventory: the acervo belongs to nobody.
   */
  ownerDisplayName: string | null;
  /**
   * The owner's id under the same rule as {@link Document.ownerDisplayName}:
   * present only on a third person's row inside the admin inventory. It is how
   * the console asks for THAT owner's area vocabulary (`GET
   * /v1/knowledge/areas?user=`) when the admin refiles a document of theirs —
   * the PATCH bounds the choice by the document's owner, never by the caller.
   */
  ownerUserId: UUID | null;
}

/**
 * Who a document answers — the three levels the API enforces in every search:
 *
 * - `private` — the DEFAULT for every role, admins included: only its owner, and
 *   only in a DIRECT chat (the console, or a DM with the bot's number). Never in
 *   a group, not even the owner's own;
 * - `public` — its owner, plus the registered WhatsApp groups the owner is in
 *   (as the group's owner or as a participant). The owner switches a document
 *   between `private` and `public` at will;
 * - `global` — the platform's acervo, answering everybody everywhere. Admin-only
 *   in both directions; the API answers 403 to anybody else, editors included,
 *   which is why the console hides the option instead of offering a choice that
 *   cannot work;
 * - `site` — the public site's widget corpus (`POST /public/site-chat`), and ONLY
 *   that: no console, Shiba or WhatsApp search reads it, and the widget reads
 *   nothing else. Admin-only, under the same rule as `global`.
 */
export type DocumentVisibility = 'private' | 'public' | 'global' | 'site';

/** Metadata sent alongside an uploaded file (all optional; sensible defaults). */
export interface UploadDocumentMeta {
  title?: string;
  source?: string;
  docType?: DocType;
  /** Knowledge-area slug to file the document under; omitted leaves it global. */
  area?: string;
  version?: string | null;
  /** Destination shelf; omitted means `private`, like the API's own default. */
  visibility?: DocumentVisibility;
}

/**
 * Outcome of {@link ApiClient.uploadDocument}: the API **accepted** the file (202)
 * and is indexing it in the background. There is no "indexed" variant — extracting,
 * chunking and embedding a document takes longer than the gateway will hold a
 * request open, so no upload is ever finished when the call returns. `runId` is the
 * ingestion run to follow on the Ingestão tab; `documentId` is the id the document
 * will have once that run succeeds.
 */
export interface UploadDocumentResult {
  documentId: UUID;
  runId: UUID;
}

export type IngestionStatus = 'running' | 'succeeded' | 'failed';

export interface IngestionRun {
  id: UUID;
  startedAt: ISODateTime;
  finishedAt: ISODateTime | null;
  status: IngestionStatus;
  docsProcessed: number;
  chunksCreated: number;
  /** Totals the run must process; while running, the processed counts above
   *  reflect real progress (updated per batch), so these enable a determinate
   *  progress bar. `chunksTotal` is 0 until the corpus is counted. */
  docsTotal: number;
  chunksTotal: number;
  /** What the run is processing — the uploaded file's name. `null` for a reindex
   *  (no single subject) and for runs recorded before the column existed. */
  label: string | null;
  /** When the run passed the server's ingestion gate. `null` while it is still
   *  waiting its turn — a `running` run with no `workStartedAt` is QUEUED, which
   *  is a normal state and must never be shown as a problem. */
  workStartedAt: ISODateTime | null;
  /** Last proof of life from the process working the run (refreshed every 30s).
   *  The server closes runs that go silent, so the console never has to guess. */
  heartbeatAt: ISODateTime | null;
  /** The run's closing note: the failure reason when `status` is `failed`, or why
   *  a `succeeded` run indexed nothing (the file was already in the base). */
  error: string | null;
}

/**
 * The ingestion-runs list envelope. `runningCount` is the number of runs in
 * flight across the WHOLE history, not just the page in hand: a batch upload can
 * be larger than a page, so counting the visible rows would understate the banner
 * — and stop the polling early — depending on which page happens to be open.
 */
export interface IngestionRunsPage extends Page<IngestionRun> {
  runningCount: number;
}

export interface ReindexInput {
  scope: 'all' | 'document';
  documentId?: UUID;
}

// ---- Governance: QA --------------------------------------------------------

export type QAChannel = 'whatsapp' | 'web';

/**
 * Depth of a generated answer. `concisa` is the default (short, to the point);
 * `completa` returns a longer answer with more technical detail (and, in the
 * catalog, tends to surface an extra web source).
 */
export type AnswerLevel = 'concisa' | 'completa';

export interface Citation {
  documentId: UUID;
  documentTitle: string;
  chunkId: UUID | null;
  ordinal: number | null;
  snippet: string;
  /**
   * Source URL of a web result (`documentId === "web"`), the only kind the
   * console links. For a catalog document it is the storage key — sent to an
   * admin only; everyone else receives null, because a key is a handle on a file
   * they may not download.
   */
  uri?: string | null;
  /**
   * Whether the source is on the READER's own shelf — what lets the chat say
   * "da sua base" beside it. A boolean, never the owner. False for global and
   * web sources, and absent/false in the interaction history, which does not
   * record a shelf.
   */
  ownedByMe?: boolean;
}

export interface QAInteraction {
  id: UUID;
  channel: QAChannel;
  userRef: string | null;
  question: string;
  answer: string;
  sources: Citation[];
  confidence: number;
  createdAt: ISODateTime;
}

export interface AskInput {
  question: string;
  channel?: QAChannel;
  /** Answer depth. Defaults to `concisa` when omitted. */
  level?: AnswerLevel;
  /**
   * Specialist agent to answer: `auto` (or omitted) routes by @handle / classifier /
   * fallback; an explicit persona id pins the answer to that agent.
   */
  agent?: string;
  /**
   * Knowledge areas the person deliberately chose to search in (slugs). This is
   * the ONE hard filter in the retrieval path: sending it narrows the corpus to
   * those areas and nothing else. Omit it (the normal case) and the search stays
   * global — the persona's own areas only ever bias the ranking, never gate it.
   */
  areas?: string[];
  /**
   * Banca de referência SÓ para esta pergunta, sobrepondo a preferência do
   * leitor. O console não a usa — lá o seletor PERSISTE a escolha, porque uma
   * banca que valesse só para a próxima pergunta seria uma configuração que a
   * pessoa teria de refazer a cada turno. Existe para os canais em que a
   * pergunta é a única coisa que viaja.
   */
  examBoard?: string;
  /**
   * The thread this question continues. Absent opens a NEW thread (the reply
   * says which); another person's or an archived one is 404, a WhatsApp group
   * thread 409 — groups are only read from the console.
   */
  conversationId?: UUID;
  /** Where a NEW thread is opened (`knowledge` by default). A continued thread
   *  keeps its own surface, so this is ignored alongside `conversationId`. */
  surface?: AskSurface;
  /**
   * When true the API answers as an SSE stream (meta → delta* → done) instead of
   * a single JSON body — consumed via `ApiClient.askStream`, never `ask`.
   */
  stream?: boolean;
}

export interface AskResult {
  interactionId: UUID;
  answer: string;
  sources: Citation[];
  confidence: number;
  conversationId: UUID;
}

/**
 * One event of the POST /v1/knowledge/ask SSE stream (`stream: true`). Order is
 * fixed: exactly one `meta`, then any number of `delta`, then `done`. Backend
 * errors degrade to a normal stream (the fallback message arrives as deltas) —
 * only network/HTTP failures reject the generator.
 */
export type AskStreamEvent =
  | { type: 'meta'; citations: Citation[]; confidence: number; usedContext: boolean }
  | { type: 'delta'; text: string }
  | { type: 'done'; interactionId: UUID; truncated: boolean };

/**
 * An image attached to a question, exactly as `AskImageInput` reads it on the
 * wire: the backend field is `data_b64` on a CamelModel, so the JSON key is
 * `dataB64` — raw base64 of the file's bytes, WITHOUT the `data:` prefix —
 * plus the mimetype the browser reported for it.
 */
export interface AskImage {
  dataB64: string;
  mimetype: string;
}

/**
 * Input of the ASYNC ask (`POST /v1/knowledge/asks`): the same fields as
 * {@link AskInput} minus `stream` — the answer is produced in the background
 * because the API Gateway cuts any request that runs past ~30s.
 *
 * `image` rides only here, never on the synchronous/streaming ask: describing an
 * image adds a whole vision call before the reasoning one, which is exactly the
 * kind of run the gateway would cut. Omit the field when there is no attachment
 * — an empty object on the wire would read as "an image I could not decode".
 */
export type AskAsyncInput = Omit<AskInput, 'stream'> & {
  image?: AskImage;
};

export type AskJobStatus = 'running' | 'done' | 'failed';

/**
 * The 202 receipt of `POST /v1/knowledge/asks`: the question was accepted and
 * is being processed off the request. `stage` is the first friendly progress
 * line (pt-BR); poll {@link ApiClient.getAsk} for the outcome.
 */
export interface AskAccepted {
  askId: UUID;
  status: AskJobStatus;
  stage: string | null;
  /** The thread the turn lands in — the only place it comes back: the polling
   *  GET ({@link AskJob}) does not carry it, so the surface keeps this one. */
  conversationId: UUID;
}

/**
 * `GET /v1/knowledge/asks/{askId}` — the full status of a background ask run.
 * A run takes 1–3 minutes: while `running` only `stage` is meaningful; on
 * `done` the answer fields are set (and `interactionId` unlocks feedback); on
 * `failed` `error` carries a friendly pt-BR reason.
 */
export interface AskJob {
  askId: UUID;
  status: AskJobStatus;
  /** Friendly progress line while running (pt-BR); null otherwise. */
  stage: string | null;
  answer: string | null;
  sources: Citation[] | null;
  confidence: number | null;
  /** Set once `done` — only persisted answers can receive feedback. */
  interactionId: UUID | null;
  /** Friendly pt-BR failure reason when status is `failed`. */
  error: string | null;
}

// ---- Conversations (history) -------------------------------------------------

/** Where a thread lives. The WhatsApp ones are opened by the bot, never here. */
export type ConversationSurface = 'knowledge' | 'chatbot' | 'whatsapp_direct' | 'whatsapp_group';

/** The surfaces a console ask may open a thread on. */
export type AskSurface = Extract<ConversationSurface, 'knowledge' | 'chatbot'>;

/**
 * As origens que o bot do WhatsApp abre. As conversas delas são do WhatsApp: o
 * console as fixa como qualquer outra, mas não as renomeia nem as exclui (a API
 * responde 409 `whatsapp_readonly`).
 */
export const WHATSAPP_SURFACES: readonly ConversationSurface[] = [
  'whatsapp_direct',
  'whatsapp_group',
];

/** O teto da API para o título de uma conversa, contado depois de aparado. */
export const MAX_CONVERSATION_TITLE = 120;

/** One thread of the caller's history — the side list and the search page. */
export interface Conversation {
  id: UUID;
  surface: ConversationSurface;
  /** "Nova conversa" until the first question names it. */
  title: string;
  /** The contact or group the WhatsApp thread belongs to; null on the console. */
  externalRef: string | null;
  lastMessageAt: ISODateTime;
  createdAt: ISODateTime;
  /** True only for a WhatsApp group: shown, never continued from the console. */
  readOnly: boolean;
  /** Quando a pessoa fixou a conversa no topo da lista; `null` quando não está fixada. */
  pinnedAt: ISODateTime | null;
  /**
   * A ÚLTIMA resposta da conversa, cortada em 160 caracteres e terminada em "…"
   * quando cortada: o que a busca mostra sob o título. `null` enquanto não há turno.
   */
  preview: string | null;
}

/**
 * `GET /v1/conversations` — as fixadas primeiro (a fixada há menos tempo no
 * topo) e depois as demais, da mais recente para a mais antiga por
 * `lastMessageAt`. As fixadas vêm SÓ na primeira página (sem `before`); as
 * páginas atrás de um cursor trazem apenas conversas não fixadas.
 */
export interface ConversationPage {
  items: Conversation[];
  /** The `before` of the next page; null on the last one. */
  nextBefore: ISODateTime | null;
}

export interface ConversationListParams {
  surface?: ConversationSurface;
  /** Searches the title and every turn's question and answer (≤ 200 chars). */
  q?: string;
  /** Exclusive cursor: the previous page's `nextBefore`. */
  before?: ISODateTime;
  /** 1–100, 50 by default. */
  limit?: number;
}

/**
 * `PATCH /v1/conversations/{id}` — os dois campos são opcionais. A API apara o
 * `title`, que precisa ficar com 1 a {@link MAX_CONVERSATION_TITLE} caracteres
 * (senão, 422); numa conversa do WhatsApp ele é recusado (409
 * `whatsapp_readonly`). `pinned` fixa ou desafixa em qualquer origem.
 */
export interface ConversationPatch {
  title?: string;
  pinned?: boolean;
}

/** Half of a recorded turn: the question (`user`) or the answer (`assistant`). */
export interface ConversationMessage {
  /** `<interactionId>:q` / `<interactionId>:a`. */
  id: string;
  role: 'user' | 'assistant';
  text: string;
  at: ISODateTime;
  interactionId: UUID;
  /** Who asked, on the question of a WhatsApp group thread. */
  senderName?: string | null;
  /** The persona that signed the answer (null = the asker's official voice). */
  agentId?: string | null;
  /** On answers; null on questions. */
  sources?: Citation[] | null;
}

/** `GET /v1/conversations/{id}` — oldest first, the last 200 turns at most. */
export interface ConversationDetail {
  conversation: Conversation;
  messages: ConversationMessage[];
}

/** `POST /v1/knowledge/transcribe` — the microphone clip as text. */
export interface TranscriptionResult {
  text: string;
}

/** Binary vote on an answer: 1 = useful (👍), -1 = not useful (👎). */
export type FeedbackRating = 1 | -1;

export interface FeedbackInput {
  rating: FeedbackRating;
  /** Optional short "why" note (the API caps it at 500 chars). */
  comment?: string;
}

export interface FeedbackResult {
  id: UUID;
}

// ---- Ops: decks ------------------------------------------------------------

export type DeckStatus = 'requested' | 'rendering' | 'ready' | 'failed';

export interface Deck {
  id: UUID;
  requestedBy: string;
  title: string;
  sourceText: string;
  status: DeckStatus;
  artifactUri: string | null;
  /** Optional brand colours (#RRGGBB) chosen at creation; null = default palette. */
  colorPrimary: string | null;
  colorSecondary: string | null;
  /** Failure reason shown in the console when status is 'failed'. */
  error: string | null;
  createdAt: ISODateTime;
  /** Bumped on each status transition; drives the live progress display. */
  updatedAt: ISODateTime | null;
}

export interface DeckInput {
  title: string;
  sourceText: string;
  /** Optional two brand colours; omit for the default corporate palette. */
  colorPrimary?: string | null;
  colorSecondary?: string | null;
}

/** Edit payload: enrich an existing deck's text/colours and re-render in place. */
export interface DeckUpdateInput {
  title: string;
  sourceText: string;
  colorPrimary?: string | null;
  colorSecondary?: string | null;
}

// ---- Ops: trainings --------------------------------------------------------

/**
 * A "treinamento" ingests one source document about a theme and derives a
 * knowledge pack from it: raw chunks, a summary, Q&A pairs and extracted facts.
 * Status advances requested → processing → ready (or failed).
 */
export type TrainingStatus = 'requested' | 'processing' | 'ready' | 'failed';

export interface Training {
  id: UUID;
  theme: string;
  /** Source document produced from the uploaded file, once the pack is built. */
  documentId: UUID | null;
  status: TrainingStatus;
  /** Model that generated the pack (fixed server-side); absent on older rows. */
  model?: string | null;
  rawChunks: number;
  summaryChunks: number;
  qaPairs: number;
  factChunks: number;
  /** Failure reason shown in the console when status is 'failed'. */
  error: string | null;
  createdAt?: ISODateTime | null;
}

/**
 * Create payload: a theme plus the source file (.pdf/.docx), sent multipart.
 *
 * Member+ since the enrichment left the admin-only shelf: `visibility` and
 * `area` say whose shelf the derived pack lands on and where it is filed,
 * exactly like an upload's. Omitted, the API defaults to the caller's private
 * shelf with no area (an admin's call still defaults to the acervo).
 */
export interface TrainingInput {
  theme: string;
  file: File;
  /** Destination shelf of the derived documents; `global` is admin-only (403). */
  visibility?: DocumentVisibility;
  /** Area slug of that shelf's vocabulary — the caller's own areas included. */
  area?: string | null;
}

/**
 * `POST /v1/documents/{id}/enrich` — enrich an already-indexed document WITHOUT
 * reindexing it: the source keeps its chunks and its `indexedAt`, and the pack
 * (summary, Q&A, facts) is derived from the retained file onto the same shelf.
 */
export interface EnrichDocumentInput {
  theme: string;
}

// ---- Governance: users -----------------------------------------------------

export type UserStatus = 'active' | 'disabled';

export interface AppUser {
  id: UUID;
  email: string;
  displayName: string;
  role: Role;
  status: UserStatus;
  /** The profile this account holds — where its quotas and features come from. */
  profileSlug: string;
  createdAt: ISODateTime;
  lastLoginAt: ISODateTime | null;
  /**
   * The invitation has not been claimed by a sign-in yet — the only rows that
   * may be re-invited. The server derives it from the placeholder subject it
   * keeps to itself: the provider's identifier for a person never crosses the
   * wire, and for a pending row it would be the invitee's e-mail spelled twice.
   */
  invitePending: boolean;
}

export interface UserInput {
  email: string;
  displayName: string;
  role: Role;
}

export interface UserPatch {
  role?: Role;
  status?: UserStatus;
  /** Move the account to another profile — its quotas and features follow. */
  profileSlug?: string;
  displayName?: string;
}

/** GET /v1/me — the authenticated principal. */
/**
 * O plano de assinatura — o eixo COMERCIAL do que a conta pode fazer, já
 * resolvido pelo status: um Pro em `pending_payment` chega com as
 * capabilities do free. Convive com {@link UserCapabilities} (o eixo do
 * PERFIL — as quotas de cada superfície) porque respondem perguntas
 * diferentes: o plano diz se a superfície existe, o perfil diz quanto dela.
 */
export interface CurrentPlan {
  code: string;
  name: string;
  status: string;
  capabilities: string[];
  tokenLimitMonth: number;
}

/** O consumo do mês corrente (virada no relógio local), para o painel do gratuito. */
export interface CurrentTokenUsage {
  /** O mês da janela, `AAAA-MM` no relógio local. */
  month: string;
  tokensUsed: number;
  tokenLimitMonth: number;
  tokensRemaining: number;
}

export type BillingType = 'PIX' | 'CREDIT_CARD';
export type BillingCycle = 'MONTHLY' | 'YEARLY';

export interface BillingPlan {
  code: string;
  name: string;
  priceCentsMonth: number;
  priceCentsYear: number | null;
  introPriceCentsMonth: number | null;
  introMonths: number | null;
  trialDays: number;
  tokenLimitMonth: number;
  capabilities: string[];
}

/** O catálogo vem embrulhado: `{ plans: [] }`, nunca um array nu. */
export interface BillingPlansResponse {
  plans: BillingPlan[];
  /**
   * Se a contratação online está aberta (só com o gateway de pagamento
   * configurado). Falso — ou ausente, num backend anterior ao campo —, os CTAs
   * pagos pedem acesso por e-mail em vez de vender um checkout que terminaria em 503.
   */
  checkoutEnabled: boolean;
}

export interface BillingPix {
  encodedImage: string;
  copyPaste: string;
  expirationDate: string | null;
}

export interface BillingSubscription {
  planCode: string;
  planName: string;
  status: string;
  cycle: BillingCycle | null;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  introEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
  autoRenew?: boolean;
  cardBrand: string | null;
  cardLast4: string | null;
  /** Só em `pending_payment`. */
  pix: BillingPix | null;
}

export interface BillingPayment {
  id: UUID;
  description: string | null;
  amountCents: number;
  status: string;
  billingType: BillingType | null;
  paidAt: ISODateTime | null;
  dueDate: string | null;
  invoiceUrl: string | null;
}

export interface BillingPaymentsResponse {
  payments: BillingPayment[];
}

export interface BillingHolderInput {
  name: string;
  cpfCnpj: string;
  postalCode: string;
  addressNumber: string;
  phone: string;
}

export interface BillingCardInput {
  holderName: string;
  number: string;
  expiryMonth: string;
  expiryYear: string;
  ccv: string;
}

/** `card` é obrigatório em `CREDIT_CARD`; o backend responde 422 sem ele. */
export interface BillingCheckoutInput {
  billingType: BillingType;
  cycle: BillingCycle;
  holder: BillingHolderInput;
  card?: BillingCardInput | null;
  autoRenew?: boolean;
}

export interface BillingReferralCredit {
  id: UUID;
  percent: number;
  status: string;
  amountCents: number | null;
  createdAt: string;
  appliedAt: string | null;
  revokedAt: string | null;
}

export interface BillingReferrals {
  code: string | null;
  invited: number;
  converted: number;
  availablePercent: number;
  credits: BillingReferralCredit[];
}

/** Campanha pública editável em runtime pela administração. */
export interface MarketingPopup {
  enabled: boolean;
  title: string;
  description: string;
  priceLabel: string;
  imageAlt: string;
  imageUrl: string | null;
  revision: string;
}

export interface MarketingPopupPatch {
  enabled?: boolean;
  title?: string;
  description?: string;
  priceLabel?: string;
  imageAlt?: string;
  removeImage?: boolean;
  generatedAssetId?: string;
}

export interface GeneratedImageAsset {
  assetId: string;
  imageUrl: string;
  downloadUrl: string;
  provider: string;
}

export interface SiteNavigationItem {
  label: string;
  href: string;
}

export interface SiteContent {
  navigation: SiteNavigationItem[];
  home: {
    profilesTitle: string;
    featuresTitle: string;
    stepsTitle: string;
    aboutTitle: string;
    aboutBody: string;
    purposeTitle: string;
    purposeBody: string;
  };
  footer: {
    legalText: string;
    youtubeUrl: string | null;
    instagramUrl: string | null;
    tiktokUrl: string | null;
  };
}

export interface SiteContentResponse extends SiteContent {
  revision: string;
}

export interface SocialPostInput {
  prompt: string;
  caption: string;
  batchLabel: string;
  includeLogo: boolean;
}

export interface SocialPost extends SocialPostInput {
  id: string;
  batchId: string;
  provider: string;
  createdAt: string;
  imageUrl: string;
  downloadUrl: string;
}

export interface SocialSettings {
  defaultCaption: string;
  defaultIncludeLogo: boolean;
}

export interface SocialLibrary {
  posts: SocialPost[];
  settings: SocialSettings;
}

export interface CurrentUser {
  id: UUID | null;
  email: string | null;
  displayName: string | null;
  /** URL assinada da foto atual; ausente durante o rollout ou sem foto. */
  avatarUrl?: string | null;
  role: Role;
  /**
   * Opcionais só pela compatibilidade com o backend ANTERIOR ao lote de
   * assinaturas: no rollout o frontend novo sobe antes do backend e, até ele
   * virar, o /v1/me chega sem os dois. Ausentes, nada se bloqueia — sem plano,
   * sem gating.
   */
  plan?: CurrentPlan;
  tokenUsage?: CurrentTokenUsage;
  /** The profile on the record, named so the console can say which one it is. */
  profile: { slug: string; label: string };
  /**
   * What that profile allows, resolved server-side. The console reads the
   * numbers from here instead of deriving them from the profile's slug: the
   * ceilings are administrable data now, and a copy of the rule in the frontend
   * would be wrong the first time an admin edits a row.
   */
  capabilities: UserCapabilities;
  /**
   * A persona OFICIAL que a pessoa escolheu para falar com ela; `null` quando
   * nunca escolheu nenhuma. É uma preferência, não uma garantia: uma persona
   * desabilitada (ou reprovada depois da escolha) continua guardada aqui e deixa
   * de responder — por isso {@link effectiveDefaultPersonaId} existe ao lado.
   */
  defaultPersonaId: string | null;
  /**
   * A voz que o roster REALMENTE vai usar: a escolhida quando ela ainda está
   * visível, senão a voz oficial do perfil (um Agente padrão). Servida pelo servidor
   * porque a regra é dele — derivá-la aqui seria reimplementar o roster no
   * console e errar no primeiro dia em que a regra mudar.
   */
  effectiveDefaultPersonaId: string;
  /** A banca de referência do leitor (slug), ou `null` — ver {@link ExamBoard}. */
  examBoard: string | null;
  /** Opcionais durante o rollout do backend da área da conta (migration 0070). */
  profession?: string | null;
  conversationInstructions?: string | null;
  notifySystem?: boolean;
  notifyResponses?: 'never' | 'background' | 'always';
  notifyPermissions?: boolean;
}

/**
 * Uma banca de referência: o estilo de prova que a resposta imita (enunciado,
 * pegadinha típica, formato do exercício final).
 *
 * O dicionário é VERSIONADO no servidor (`agents/exam_boards.py`), não uma
 * tabela: o texto que instrui o modelo viaja com o deploy, e o console só
 * escolhe entre o que o perfil do leitor oferece.
 */
export interface ExamBoard {
  slug: string;
  label: string;
  /** O perfil dono da banca: `concursos` ou `vestibular`. */
  profile: string;
}

/** `GET /v1/exam-boards` — vazio para um perfil sem `features.examBoards`. */
export interface ExamBoardsResponse {
  boards: ExamBoard[];
}

/**
 * `PATCH /v1/me/preferences` — as duas escolhas que o leitor faz sobre si.
 *
 * Os dois campos são opcionais e independentes: `null` LIMPA a escolha e o campo
 * ausente não é tocado. É por isso que o console monta o corpo com a chave que
 * ele está mudando, e só com ela — mandar `examBoard: null` junto de uma troca
 * de persona apagaria a banca de quem só queria trocar de voz.
 */
export interface PreferencesPatch {
  defaultPersonaId?: string | null;
  examBoard?: string | null;
  displayName?: string | null;
  profession?: string | null;
  conversationInstructions?: string | null;
  notifySystem?: boolean;
  notifyResponses?: 'never' | 'background' | 'always';
  notifyPermissions?: boolean;
}

/** `POST /v1/me/signup-context`: perfil e aceite confirmados no primeiro acesso. */
export interface SignupContextInput {
  profileSlug: string;
  termsAcceptedAt: ISODateTime;
  termsVersion: string;
}

// ---- Metrics ---------------------------------------------------------------

/** This month's LLM spend and what it projects to (admin only). */
export interface MonthCost {
  currentMonthUsd: number;
  projectedMonthUsd: number;
  /** 0 means no budget is configured — the console then shows no budget at all. */
  budgetUsd: number;
  /** Spend over budget so far; null without a budget (a ratio against zero). */
  budgetUsedRatio: number | null;
  projectedOverBudget: boolean;
  tokensIn: number;
  tokensOut: number;
}

/** The admin's overview: the whole install, over explicit windows. */
export interface PlatformOverview {
  scope: 'platform';
  activeUsers: number;
  /** Questions asked in the last 24 hours, on every channel. */
  qaInteractions24h: number;
  avgConfidence: number;
  avgLatencyMs: number;
  /** The window both quality numbers above are averaged over. */
  qualityWindowDays: number;
  documentsIndexed: number;
  lastIngestionAt: ISODateTime | null;
  cost: MonthCost;
}

/**
 * Everyone else's overview: their own shelf and their own questions. A separate
 * shape rather than the platform one with blanks, so a field that could carry
 * cost, tokens or platform numbers simply does not exist here.
 */
export interface PersonalOverview {
  scope: 'personal';
  documentsOwned: number;
  /** The private-shelf ceiling this account's PROFILE grants — data, not a constant. */
  quotaLimit: number;
  /** Questions this account asked, on any channel, inside `windowDays`. */
  questionsAsked: number;
  windowDays: number;
  /** The last of the reader's OWN ingestions to finish. */
  lastIngestionAt: ISODateTime | null;
}

/** `GET /v1/metrics/overview`, told apart by `scope`: the role decides which. */
export type MetricsOverview = PlatformOverview | PersonalOverview;

export interface CostPoint {
  date: ISODate;
  amountUsd: number;
  tokensIn: number;
  tokensOut: number;
}

/**
 * Uma linha de uma abertura do custo: o modelo ou a funcionalidade que `label`
 * nomeia e o que ela gastou no mês. `tokens` soma entrada e saída.
 */
export interface CostBucket {
  label: string;
  calls: number;
  tokens: number;
  usd: number;
}

/** `GET /v1/metrics/cost` — admin only; the API answers 403 to anybody else. */
export interface MetricsCost {
  currentMonthUsd: number;
  projectedMonthUsd: number;
  budgetUsd: number;
  series: CostPoint[];
  /** O gasto do mês por modelo, do mais caro ao mais barato, em no máximo oito linhas. */
  byModel: CostBucket[];
  /**
   * O gasto do mês por funcionalidade (`ask`, `deck`, `vision`…), na mesma ordem e
   * com o mesmo teto. A chamada que a API não soube atribuir chega como `outros`.
   */
  byFeature: CostBucket[];
}

// ---- Dashboard -------------------------------------------------------------

/** Whose activity a summary describes: the reader's own, or the platform's (admin). */
export type DashboardScope = 'personal' | 'platform';

/** A document (re)indexed recently. */
export interface RecentDocument {
  id: UUID;
  title: string;
  area: string | null;
  /** `not_indexed` when the document has no chunks the assistant can retrieve. */
  status: 'indexed' | 'not_indexed';
  chunkCount: number;
  indexedAt: ISODateTime | null;
  visibility: DocumentVisibility;
  ownedByMe: boolean;
}

/** One question asked, as a line in a list — never the answer. */
export interface RecentConversation {
  id: UUID;
  /** The question cut to a preview (≤140 chars, ending in "…" when cut). */
  question: string;
  channel: QAChannel;
  /** `null` (ou vazio) quando nenhum especialista foi roteado: respondeu a voz oficial do perfil. */
  agentId: string | null;
  agentName: string;
  createdAt: ISODateTime | null;
}

/**
 * How many answers one persona signed inside the summary's window. O balde sem
 * `agentId` é o das respostas da voz oficial de cada perfil: não é uma persona
 * só, porque cada perfil tem a sua.
 */
export interface PersonaUsage {
  agentId: string | null;
  agentName: string;
  count: number;
}

/**
 * `GET /v1/dashboard/summary` — one shape for every role; `scope` says whose
 * activity it is. Nothing here is about money or tokens.
 */
export interface DashboardSummary {
  scope: DashboardScope;
  /** The window `topPersonas` is counted over. */
  windowDays: number;
  recentDocuments: RecentDocument[];
  recentConversations: RecentConversation[];
  topPersonas: PersonaUsage[];
}

/**
 * The AI tasks whose model an admin can re-point at runtime.
 * Embeddings are intentionally absent: the pgvector column is vector(1024), so
 * the embedding model is pinned by the database DDL, not by a setting.
 * `image` is text-to-image generation (Gemini image models).
 */
export type AiModelTask = 'chat' | 'deck' | 'enrichment' | 'vision' | 'stt' | 'image';

/**
 * What a model can do. A task only ever offers models that declare the task's
 * own capability — a text model cannot describe an image, and a perception model
 * cannot hold a conversation. A model may carry several (DeepSeek V4.1 Flash is
 * `chat` + `vision`); `image` means it draws from a text prompt.
 */
export type AiModelCapability = 'chat' | 'vision' | 'stt' | 'image';

/** A selectable model; `available` = its provider's API key is configured. */
export interface AiModelOption {
  id: string;
  label: string;
  provider: string;
  available: boolean;
  capabilities: AiModelCapability[];
  /**
   * False for models whose transport pins the temperature (reasoning families)
   * and for the image-generation models.
   */
  supportsTemperature: boolean;
}

/**
 * One task's effective model and where it came from (admin override vs default
 * pin), plus the sampling temperature for the text tasks and for image
 * interpretation (`vision`). `supportsTemperature` is a fact about the EFFECTIVE
 * model, not about the task: pointing chat at a reasoning model turns the knob
 * off even though chat itself is steerable. It is always false on the tasks
 * without a knob (stt, image).
 * `temperature` is null when nothing was stored — the answer then runs at
 * `defaultTemperature` (0.1 for text, 0.0 for vision).
 */
export interface AiTaskSetting {
  task: AiModelTask;
  capability: AiModelCapability;
  defaultModel: string;
  override: string | null;
  effectiveModel: string;
  source: 'override' | 'pin';
  temperature: number | null;
  defaultTemperature: number;
  supportsTemperature: boolean;
}

export interface AiModelSettings {
  tasks: AiTaskSetting[];
  models: AiModelOption[];
}

/** The temperature half of the patch — the text tasks and image interpretation. */
export type AiTemperatureField =
  | 'chatTemperature'
  | 'deckTemperature'
  | 'enrichmentTemperature'
  | 'visionTemperature';

/**
 * Partial update: only the provided fields change; an explicit `null` clears the
 * stored value (model back to the pin, temperature back to the default).
 */
export type AiModelSettingsPatch = Partial<Record<AiModelTask, string | null>> &
  Partial<Record<AiTemperatureField, number | null>>;

/**
 * Which patch field carries each task's temperature, mirroring the backend's own
 * `TEMPERATURE_FIELDS`. STT and image generation are absent on purpose: a
 * transcript runs deterministic and a drawing takes no temperature, so there is
 * no knob to name for them. Vision entrou na Onda S (padrão 0,0).
 */
export const TEMPERATURE_FIELDS: Partial<Record<AiModelTask, AiTemperatureField>> = {
  chat: 'chatTemperature',
  deck: 'deckTemperature',
  enrichment: 'enrichmentTemperature',
  vision: 'visionTemperature',
};

/** One activity a persona can perform (a "ficha"). */
export interface SkillCard {
  id: string;
  name: string;
  description: string;
}

/**
 * What a persona may read when it answers — the HARD restriction, not the boost
 * the preferred areas used to be:
 *
 * - `all` — the whole base the asker already reaches;
 * - `areas` — only documents filed in {@link AgentPersonaPublic.scopeAreas};
 * - `linked` — only the documents explicitly linked to the persona.
 *
 * In a direct chat the asker's OWN shelf always answers, whatever the mode; in a
 * group there is no such exemption.
 */
export type PersonaScopeMode = 'all' | 'areas' | 'linked';

/**
 * Where a persona came from: `product` ships with the platform, `admin` was
 * created in this console, `user` belongs to one member. The origin is immutable
 * and decides one thing the console renders: a product persona is edited like
 * any other but never deleted.
 */
export type PersonaOrigin = 'product' | 'admin' | 'user';

/**
 * O papel da persona, independente da origem:
 *
 * - `default` — **Agente padrão**: pode ser a voz oficial de um perfil; nunca é
 *   escolhido como especialista e só entra no elenco do perfil de que é a voz
 *   (por isso o seletor do Chat não o lista);
 * - `specialist` — **Agente especialista**: roteável e listado no seletor.
 *
 * Um agente pessoal é `origin: 'user'` com `kind: 'specialist'`: "pessoal" é a
 * origem, não um terceiro tipo.
 */
export type PersonaKind = 'default' | 'specialist';

/** The moderation verdict on the persona's authored text. Only `approved` answers. */
export type PersonaModerationStatus = 'approved' | 'review' | 'rejected';

/** At most five tags, each a kebab-case ASCII token of at most 24 chars. */
export const MAX_PERSONA_TAGS = 5;
export const MAX_PERSONA_TAG_LENGTH = 24;
/** The API's ceiling on a persona's `areas` scope. */
export const MAX_PERSONA_SCOPE_AREAS = 20;
/**
 * How many product+admin personas one profile may hold. Every enabled persona of
 * the asker's roster enters the routing classifier's catalogue on EVERY message,
 * so the size of the cast is the size of that prompt.
 */
export const MAX_PERSONAS_PER_PROFILE = 25;
/** The API's ceiling on one `PUT /v1/agents/{id}/documents` payload. */
export const MAX_PERSONA_DOCUMENT_IDS = 1000;
/** The API's ceiling on one page of persona-document candidates. */
export const PERSONA_DOCUMENTS_MAX_PAGE_SIZE = 200;
/**
 * How many filtered candidates "select all" will link in one go. Past it the
 * console stops offering the shortcut and suggests the `areas` mode instead: a
 * thousand hand-picked rows is a scope that should have been a rule.
 */
export const PERSONA_SELECT_ALL_LIMIT = 500;

/**
 * A persona as every role may see it: who it is and how to call it — never how
 * it is instructed. This is what the chat's agent selector needs, and all a
 * non-admin ever receives.
 */
export interface AgentPersonaPublic {
  id: string;
  name: string;
  role: string;
  description: string;
  handles: string[];
  skills: string[];
  enabled: boolean;
  /** The profile whose roster this persona belongs to (`padrao` answers everybody). */
  profile: string;
  tags: string[];
  scopeMode: PersonaScopeMode;
  /** Leaf (subarea) slugs the `areas` mode restricts to; the boost in `all` mode. */
  scopeAreas: string[];
  /** Whether the persona adapts its answers to the asker's exam board. */
  boardAware: boolean;
  origin: PersonaOrigin;
  kind: PersonaKind;
  moderationStatus: PersonaModerationStatus;
  /**
   * `true` apenas nas personas do PRÓPRIO leitor, que vêm no roster em qualquer
   * status. É a marca que separa "uma persona da plataforma" de "uma persona
   * minha, ainda em análise": sem ela o seletor do chat não teria como oferecer
   * uma e desabilitar a outra. Ausente em tudo que não é do leitor.
   */
  mine?: boolean;
  /** O motivo da moderação, quando há um — só chega nas personas do leitor. */
  moderationReason?: string | null;
}

/**
 * Uma persona do PRÓPRIO leitor, como `/v1/me/personas` a devolve.
 *
 * É a projeção pública mais o que só o dono pode ver: por que a moderação
 * decidiu o que decidiu (`moderationReason`, `moderationFlags`) e quantos
 * documentos a persona alcança. Nenhuma dessas informações viaja para outra
 * pessoa — uma persona de terceiro simplesmente não existe do lado de fora.
 */
export interface MyPersona extends AgentPersonaPublic {
  mine: true;
  moderationReason: string | null;
  /** As categorias que a moderação marcou (`medicamentos`, `violencia`, …). */
  moderationFlags: string[];
  linkedDocumentCount: number;
  /**
   * O texto que o dono escreveu, devolvido para ele.
   *
   * É a única listagem do produto que carrega um prompt fora do console do
   * administrador, e ela só devolve linhas do próprio chamador — então o único
   * prompt que trafega é o que essa pessoa digitou. Sem ele o editor abriria em
   * branco a cada reabertura: dava para SUBSTITUIR as instruções, nunca para
   * revisá-las. O roster ({@link AgentPersonaPublic}, o que o seletor do chat
   * mostra para todo mundo) continua sem nenhum.
   */
  systemPrompt: string;
  conversationStyle: string;
}

/**
 * `POST /v1/me/personas`: a persona criada e, quando o dono tem grupo ativo, o
 * aviso de que ela começa DESLIGADA nos grupos. Na mesma transação da criação,
 * os grupos em "Todos" viram uma lista com o elenco de antes, sem o agente novo;
 * o texto vem do servidor para o console não repetir uma regra que é dele.
 */
export interface MyPersonaCreated extends MyPersona {
  groupsNotice: string | null;
}

/** `GET /v1/me/personas`: as personas do leitor e o teto do perfil dele. */
export interface MyPersonasResponse {
  personas: MyPersona[];
  /** `profile.userPersonasLimit` do chamador; `0` proíbe personas próprias. */
  limit: number;
}

/**
 * O corpo editável de uma persona de usuário — o que o dono escreve.
 *
 * Não há handles, fichas, perfil nem banca aqui, e a ausência é a regra: o
 * perfil é imposto pelo servidor (é o do chamador), handles acordariam a persona
 * no grupo de outra pessoa, e uma ficha é cabeada no repositório.
 */
export interface PersonaUserInput {
  name: string;
  role: string;
  description: string;
  systemPrompt: string;
  conversationStyle?: string;
  scopeMode?: PersonaScopeMode;
  /** Folhas da plataforma; obrigatório e não vazio quando `scopeMode` é `areas`. */
  scopeAreas?: string[];
  tags?: string[];
}

/** `POST /v1/me/personas`: o id é escolhido por quem cria e não muda depois. */
export interface PersonaUserCreate extends PersonaUserInput {
  id: string;
}

/**
 * `PUT /v1/me/personas/{id}`: o mesmo corpo sem o id, com tudo opcional.
 *
 * Opcional porque o console manda duas coisas diferentes por aqui: a edição
 * inteira do Sheet e o toggle isolado (`{enabled}`). Só uma alteração em campo
 * JULGADO (nome, papel, descrição, prompt, estilo) re-modera — ligar, desligar,
 * mexer em tags ou no escopo não devolve a persona para a fila.
 */
export interface PersonaUserPut extends Partial<PersonaUserInput> {
  enabled?: boolean;
}

/** Quem deu o veredito: a pré-triagem, o classificador, o admin, ou uma falha. */
export type ModerationSource = 'prescreen' | 'llm' | 'admin' | 'error';

/**
 * Uma linha da fila do administrador. Traz o DONO — que é o que a fila tem e o
 * roster não tem — e o veredito inteiro, inclusive de onde ele veio: uma
 * rejeição da pré-triagem e uma da mão do admin exigem leituras diferentes.
 */
export interface ModerationItem {
  id: string;
  name: string;
  role: string;
  description: string;
  profile: string;
  origin: PersonaOrigin;
  /** `null` para uma persona de produto ou de administrador: ninguém a "tem". */
  ownerUserId: string | null;
  ownerDisplayName: string | null;
  moderationStatus: PersonaModerationStatus;
  moderationFlags: string[];
  moderationReason: string | null;
  moderationSource: ModerationSource | null;
  createdAt: ISODateTime;
  moderatedAt: ISODateTime | null;
}

/**
 * A mesma linha aberta: o texto que a moderação julgou. É só aqui que o prompt
 * de uma persona de terceiro aparece para o administrador — na lista ele não
 * viaja, porque uma fila de trinta linhas não é lugar para trinta prompts.
 */
export interface ModerationItemDetail extends ModerationItem {
  systemPrompt: string;
  conversationStyle: string;
  tags: string[];
  scopeMode: PersonaScopeMode;
  scopeAreas: string[];
}

/** Filtros de `GET /v1/moderation/personas`; sem `status`, a fila é a `review`. */
export interface ModerationListParams {
  status?: PersonaModerationStatus;
  page?: number;
  pageSize?: number;
}

/** O teto de avaliações por hora e por pessoa — cada uma custa uma chamada ao LLM. */
export const MAX_PERSONA_MODERATIONS_PER_HOUR = 10;
/** O tamanho máximo do motivo que o administrador escreve ao rejeitar. */
export const MAX_MODERATION_REASON = 500;

/**
 * Specialist persona as an admin edits it. There is one row per persona and it
 * is the whole truth: a product persona is no longer a seed with an override
 * layered on top, so every field here is editable on every origin.
 */
export interface AgentPersona extends AgentPersonaPublic {
  systemPrompt: string;
  /**
   * How the persona converses beyond what it knows: tone, length, how a message
   * closes, what to do with small talk. This is where the assistant's
   * TEMPERAMENT lives — what ships by default is the seed value of this same
   * field, so an edit replaces it rather than layering on top of it. Empty means
   * "no style block": the effective prompt is then the system prompt alone.
   */
  conversationStyle: string;
  /**
   * The EFFECTIVE system prompt of the scheduled daily group report, for the
   * persona carrying the `group-reports` ficha; `null` for every persona that
   * writes no report — which is why the console renders the field only when it
   * is not null.
   */
  reportPrompt: string | null;
  /**
   * How many documents the persona is linked to. Meaningful only in `linked`
   * mode — it is what the row shows instead of a scope nobody can see — and
   * always served, so a mode switch does not have to guess what is waiting.
   */
  linkedDocumentCount: number;
}

/** The roster as an ADMIN reads and edits it — also what every agent write answers. */
export interface AgentsResponse {
  view: 'full';
  agents: AgentPersona[];
  skills: SkillCard[];
}

/** The roster as everyone else reads it: the public projection, no prompts. */
export interface AgentsPublicResponse {
  view: 'public';
  agents: AgentPersonaPublic[];
  skills: SkillCard[];
}

/** `GET /v1/agents`, told apart by `view`: the role decides which. */
export type AgentsListing = AgentsResponse | AgentsPublicResponse;

/** The scope half of a persona write — identical on create and on update. */
export interface AgentPersonaScopeInput {
  /** Required on create; the profile must exist and be enabled (422 otherwise). */
  profile: string;
  tags?: string[];
  scopeMode?: PersonaScopeMode;
  /** Leaf slugs; required and non-empty when `scopeMode` is `areas`. */
  scopeAreas?: string[];
  boardAware?: boolean;
}

/**
 * `PUT /v1/agents/{id}` payload (admin-only) — the whole editable row.
 *
 * Every field reaches every origin, product personas included: the database row
 * IS the persona now, so there is no versioned identity underneath for an edit
 * to contradict. Only `origin` is immutable, and it is not in this payload.
 * `extraHandles` keeps its name because it keeps its meaning: it is the handle
 * list the persona answers to after the write.
 */
export interface AgentPersonaPut extends AgentPersonaScopeInput {
  systemPrompt: string;
  extraHandles: string[];
  conversationStyle: string;
  /**
   * Sent ONLY for the persona that writes the daily group report. Omitted (or
   * blank) means "keep the prompt that ships with the product" — never "run the
   * report with an empty voice", which is why this is optional rather than a
   * string the console would have to invent a value for.
   */
  reportPrompt?: string | null;
  enabled: boolean;
  name: string;
  role: string;
  description: string;
  /**
   * O papel da linha. O console sempre o manda, e o admin pode trocá-lo nos dois
   * sentidos; só um Agente padrão que é voz oficial de algum perfil não vira
   * especialista (409 com os perfis).
   */
  kind?: PersonaKind;
}

/**
 * `POST /v1/agents` payload (admin-only): a persona created at runtime. The id
 * is chosen by the admin (a slug) and is immutable afterwards — it is what the
 * chat selector and the router persist against.
 */
export interface AgentPersonaCreate extends AgentPersonaScopeInput {
  id: string;
  name: string;
  role: string;
  description: string;
  systemPrompt: string;
  conversationStyle: string;
  handles: string[];
  enabled: boolean;
  /** Ausente = `specialist`; a seção Agentes Padrão cria com `default`. */
  kind?: PersonaKind;
}

/**
 * One candidate of the persona-document picker: a GLOBAL document, plus whether
 * this persona is linked to it. Only the acervo is offered — a persona answers
 * everybody, so linking somebody's private shelf to it would be a leak with a
 * checkbox in front of it.
 */
export interface PersonaDocument {
  id: UUID;
  title: string;
  /** The leaf slug the document is filed under, or null when unclassified. */
  area: string | null;
  visibility: DocumentVisibility;
  linked: boolean;
}

/** Filters of `GET /v1/agents/{id}/documents`. `area` accepts an area or a leaf. */
export interface PersonaDocumentsParams {
  area?: string;
  search?: string;
  linked?: 'all' | 'linked' | 'unlinked';
  page?: number;
  pageSize?: number;
}

// ---- WhatsApp link ---------------------------------------------------------

/**
 * `GET /v1/me/whatsapp` (and what `POST /verify` answers with): the caller's own
 * link to a WhatsApp number.
 *
 * There is no field here that carries the full number, deliberately: the console
 * never needs it, and a type that could hold it would end up rendered, cached
 * and screenshotted. `phoneMasked` is `"+55 •• ****-5678"` — enough for somebody
 * to recognise their own phone, never enough to dial it.
 */
export interface WhatsAppLink {
  linked: boolean;
  phoneMasked: string | null;
  verifiedAt: string | null;
}

/** `POST /v1/me/whatsapp/link`: where the code went, and when it stops working. */
export interface WhatsAppLinkStarted {
  phoneMasked: string;
  expiresAt: string;
}

// ---- WhatsApp groups -------------------------------------------------------

/** `active` while the owner has a proven number; `suspended` while they do not. */
export type WhatsAppGroupStatus = 'active' | 'suspended';

/**
 * One registered group, as its OWNER sees it.
 *
 * The group id is NOT masked, unlike the phone number above, and the difference
 * is deliberate: a group id names a room rather than a person, its owner is the
 * only one who can read this list, and it is the one thing that lets somebody
 * tell their four groups apart.
 */
export interface WhatsAppGroup {
  groupId: string;
  status: WhatsAppGroupStatus;
  registeredAt: string;
  /** O nome do grupo no WhatsApp, capturado pela ponte; `null` enquanto não veio. */
  subject: string | null;
  agentsMode: WhatsAppGroupAgentsMode;
  /**
   * Os escolhidos que SEGUEM disponíveis para o dono. Só tem sentido em
   * `selected`: em `all` o grupo acompanha o elenco inteiro, inclusive os
   * agentes criados depois, e a lista não é a fonte da verdade.
   */
  agents: WhatsAppGroupAgent[];
  /** Quantos escolhidos sumiram (desabilitados, recusados, apagados, fora do perfil). */
  unavailableAgents: number;
  /**
   * Os agentes pessoais do dono (aprovados e habilitados) que NÃO estão na lista
   * de um grupo `selected`: estão no grupo, mas desligados. É assim que um agente
   * novo aparece depois de aprovado, porque ele nasce fora da lista. Vazio em
   * `all`, onde o grupo já acompanha o elenco inteiro.
   */
  availableAgents: WhatsAppGroupAgent[];
}

/**
 * `all` acompanha o elenco do dono inteiro, dinamicamente; `selected` fixa a
 * lista escolhida um a um.
 */
export type WhatsAppGroupAgentsMode = 'all' | 'selected';

/** Um agente como o grupo o mostra: o bastante para uma tag, nada do prompt. */
export interface WhatsAppGroupAgent {
  id: string;
  name: string;
  kind: PersonaKind;
  /** Um agente pessoal do dono (o "Meus agentes" do seletor). */
  mine: boolean;
}

/** Em qual bloco do seletor o candidato aparece. */
export type WhatsAppGroupAgentSection = 'voice' | 'specialists' | 'mine';

/**
 * Um agente que o dono PODE pôr num grupo: o elenco dele (voz oficial do
 * perfil, especialistas do perfil e do Padrão, agentes pessoais aprovados).
 */
export interface WhatsAppGroupAgentCandidate extends WhatsAppGroupAgent {
  section: WhatsAppGroupAgentSection;
}

/** `GET /v1/me/whatsapp/groups/agents`, na ordem do elenco. */
export interface WhatsAppGroupAgentCandidates {
  agents: WhatsAppGroupAgentCandidate[];
}

/**
 * A escolha que vai no corpo de `POST …/groups/code` e de `PUT
 * …/groups/{id}/agents`: `"all"` ou uma lista não vazia de ids candidatos.
 */
export type WhatsAppGroupAgentsChoice = 'all' | string[];

/**
 * `GET /v1/me/whatsapp/groups`: the caller's groups, their ceiling, and whether
 * they can register at all. `linked` travels with the list because an empty one
 * has two very different explanations.
 */
export interface WhatsAppGroups {
  groups: WhatsAppGroup[];
  limit: number;
  linked: boolean;
}

/**
 * `POST /v1/me/whatsapp/groups/code`: the code to post inside a group.
 *
 * This response is one of exactly two places the code exists in the clear (the
 * other is the DM the agent can send instead). It cannot be fetched again — the
 * server keeps only a salted hash — so a page that loses it has to ask for a new
 * one, which costs one of the three an hour.
 */
export interface WhatsAppGroupCode {
  code: string;
  expiresAt: string;
}

// ---- Dependentes (família) -------------------------------------------------

/**
 * Onde um dependente está no caminho até conversar com o agente.
 *
 * `pending` é o estado em que ele NASCE e não é um defeito: a linha existe, mas
 * nenhum número foi provado ainda — quem prova é o próprio dependente, mandando
 * o código `FAM-` numa conversa direta com o agente. `suspended` é a pausa que o
 * responsável dá sem apagar nada (o histórico e os lembretes continuam lá).
 */
export type DependentStatus = 'pending' | 'active' | 'suspended';

/**
 * Um dependente, como o RESPONSÁVEL o vê.
 *
 * O número vem sempre mascarado, pela mesma razão do vínculo do próprio leitor:
 * o console nunca precisa do número inteiro, e um campo capaz de carregá-lo
 * acabaria renderizado, cacheado e fotografado. Aqui o cuidado é ainda maior —
 * o número é de OUTRA pessoa, quase sempre uma criança.
 */
export interface Dependent {
  id: UUID;
  name: string;
  /** O perfil que decide o roster, a árvore e os recursos do dependente. */
  profile: { slug: string; label: string };
  status: DependentStatus;
  phoneMasked: string | null;
  boundAt: string | null;
  createdAt: string;
  /** Quantos lembretes o dependente tem — o que a linha promete ao abrir a lista. */
  remindersCount: number;
}

/** `GET /v1/me/dependents`: os dependentes do chamador e o teto do perfil dele. */
export interface DependentsResponse {
  dependents: Dependent[];
  limit: number;
}

export interface DependentCreate {
  name: string;
  profileSlug: string;
}

/**
 * Alteração parcial. `status` só alterna entre `active` e `suspended`: sair de
 * `pending` é o número sendo provado na conversa, e nenhum botão do console pode
 * fazer isso por ele.
 */
export interface DependentPatch {
  name?: string;
  profileSlug?: string;
  status?: Extract<DependentStatus, 'active' | 'suspended'>;
}

/**
 * `POST /v1/me/dependents/{id}/code`: o código que o dependente manda ao agente.
 *
 * Esta resposta é o ÚNICO lugar onde o código existe em claro — o servidor
 * guarda só um hash com sal, nenhum GET o devolve e ele não entra em log nem em
 * auditoria. Perder a tela custa um dos três códigos do dia.
 */
export interface DependentCode {
  code: string;
  expiresAt: string;
}

/** Os mesmos quatro estados que `ops.reminder` aceita no banco. */
export type DependentReminderStatus = 'pending' | 'sent' | 'cancelled' | 'failed';

/** Um lembrete de um dependente, criado por ele na conversa ou pelo responsável aqui. */
export interface DependentReminder {
  id: UUID;
  text: string;
  dueAt: string;
  status: DependentReminderStatus;
}

export interface DependentRemindersResponse {
  reminders: DependentReminder[];
}

export interface DependentReminderCreate {
  text: string;
  /** ISO 8601, sempre no futuro — um lembrete para ontem não é entregável. */
  dueAt: string;
}

// ---- Transport-level error -------------------------------------------------

export interface ApiErrorBody {
  code: string;
  message: string;
  detail?: unknown;
}

// ---- Widget público do site (POST /public/site-chat) ------------------------

/** Um turno da conversa do widget público, como o backend o recebe de volta. */
export interface SiteChatTurn {
  role: 'user' | 'assistant';
  text: string;
}

/**
 * O pedido do widget público. Sem login: o backend não grava a conversa, então o
 * histórico (no máximo {@link SITE_CHAT_MAX_TURNS} turnos, o último do visitante)
 * vai inteiro a cada mensagem, com o id de sessão do navegador.
 */
export interface SiteChatInput {
  sessionId: string;
  messages: SiteChatTurn[];
}

/** O perfil que o questionário recomendou; `slug` é o de `/perfis/<slug>`. */
export interface SiteChatRecommendation {
  slug: string;
  reason: string;
}

/** A resposta da Shiba do site: texto, respostas rápidas e, ao fim, a recomendação. */
export interface SiteChatReply {
  text: string;
  /** Até quatro respostas rápidas, como botões. */
  suggestions: string[];
  recommendation: SiteChatRecommendation | null;
  /** Os demais perfis, sem o recomendado; vazio sem recomendação. */
  others: string[];
}

/** Tetos do pedido, os mesmos do schema da API. */
export const SITE_CHAT_MAX_TURNS = 12;
export const SITE_CHAT_MAX_CHARS = 1000;
